import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Star, Loader2, Lock, Home, Brain, Package, HeartHandshake } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

export const PILLARS = [
  { id: "space", label: "Calidad del espacio", hint: "Limpieza, comodidad y adecuación del lugar", icon: Home },
  { id: "knowledge", label: "Conocimiento del Maestro", hint: "Dominio técnico y teórico de la materia", icon: Brain },
  { id: "materials", label: "Materiales utilizados", hint: "Calidad, disponibilidad y adecuación de insumos", icon: Package },
  { id: "method", label: "Metodología y trato", hint: "Claridad, paciencia, respeto y cercanía", icon: HeartHandshake },
];
const WORDS = ["", "Malo", "Regular", "Bueno", "Muy bueno", "Excelente"];

export function Stars({ value = 0, size = "w-4 h-4" }) {
  return (
    <span className="inline-flex gap-0.5" aria-label={`${value} de 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`${size} ${value >= i - 0.25 ? "fill-amber-400 text-amber-400" : value >= i - 0.75 ? "fill-amber-400/50 text-amber-400" : "text-muted-foreground/30"}`} />
      ))}
    </span>
  );
}

export function RatingSummary({ rep, testId = "maestro-reputation" }) {
  const has = rep?.total > 0;
  return (
    <Card className="p-4 sm:p-5" data-testid={testId}>
      <div className="flex items-center gap-3">
        <span className={`text-3xl font-extrabold tabular-nums ${has ? "" : "text-muted-foreground"}`} data-testid="rep-avg">{has ? rep.avg.toFixed(1) : "—"}</span>
        <div><Stars value={rep?.avg || 0} />
          <p className="text-xs text-muted-foreground mt-0.5" data-testid="rep-total">{has ? `${rep.total} ${rep.total === 1 ? "evaluación" : "evaluaciones"}` : "Sin evaluaciones aún"}</p></div>
      </div>
      {has && (
        <div className="mt-3 space-y-1.5" data-testid="rep-pillars">
          {PILLARS.map((p) => (
            <div key={p.id} className="flex items-center gap-2 text-xs" data-testid={`rep-pillar-${p.id}`}>
              <span className="w-[136px] shrink-0 truncate text-muted-foreground">{p.label}</span>
              <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden"><div className="h-full bg-amber-400 rounded-full" style={{ width: `${(rep.pillars[p.id] / 5) * 100}%` }} /></div>
              <span className="w-7 text-right font-semibold tabular-nums">{rep.pillars[p.id]?.toFixed(1)}</span>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

function StarPicker({ value, onChange, testId }) {
  const [hover, setHover] = useState(0);
  const v = hover || value;
  return (
    <div className="flex items-center gap-2">
      <div className="flex" onMouseLeave={() => setHover(0)}>
        {[1, 2, 3, 4, 5].map((i) => (
          <button key={i} type="button" onClick={() => onChange(i)} onMouseEnter={() => setHover(i)} aria-label={`${i} estrellas`}
            className="p-0.5 transition-transform active:scale-90 hover:scale-110" data-testid={`${testId}-${i}`}>
            <Star className={`w-7 h-7 ${v >= i ? "fill-amber-400 text-amber-400" : "text-muted-foreground/40"}`} />
          </button>
        ))}
      </div>
      <span className="text-xs font-semibold text-amber-700 w-16">{WORDS[v]}</span>
    </div>
  );
}

export function RateMaestroDialog({ item, onClose, onDone }) {
  const [s, setS] = useState({});
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setS({}); setComment(""); }, [item?.reservation_id]);
  const ready = PILLARS.every((p) => s[p.id]);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/maestro-reviews", { reservation_id: item.reservation_id, ...s, comment: comment || null });
      toast.success("¡Gracias por evaluar al Maestro!");
      onClose(); onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open={!!item} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto" data-testid="rate-maestro-dialog">
        <DialogHeader><DialogTitle>Evalúa a {item?.business_name || "tu Maestro"}</DialogTitle>
          <DialogDescription>Por el taller «{item?.workshop_name}». Califica cada aspecto de 1 a 5 estrellas.</DialogDescription></DialogHeader>
        <div className="space-y-4">
          {PILLARS.map((p) => (
            <div key={p.id} className="rounded-xl border p-3" data-testid={`rate-pillar-${p.id}`}>
              <p className="font-semibold text-sm flex items-center gap-1.5"><p.icon className="w-4 h-4 text-primary" />{p.label}</p>
              <p className="text-xs text-muted-foreground mb-1.5">{p.hint}</p>
              <StarPicker value={s[p.id] || 0} onChange={(v) => setS({ ...s, [p.id]: v })} testId={`rate-${p.id}`} />
            </div>
          ))}
          <div>
            <Textarea rows={3} maxLength={1000} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Comentario para el Maestro (opcional)" data-testid="rate-comment" />
            <p className="text-[11px] text-muted-foreground mt-1 flex items-center gap-1"><Lock className="w-3 h-3" />Tu comentario solo lo verá el Maestro.</p>
          </div>
          <Button className="w-full" onClick={submit} disabled={!ready || busy} data-testid="rate-submit">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : ready ? "Enviar evaluación" : "Califica los 4 aspectos"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function RateMaestroBox({ businessId, onRated }) {
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);
  const load = () => api.get(`/maestros/${businessId}/review-eligibility`).then((r) => setD(r.data)).catch(() => setD(null));
  useEffect(() => { load(); }, [businessId]); // eslint-disable-line
  if (!d || d.items.length === 0) return null;
  const pend = d.items.filter((x) => x.eligible && !x.reviewed);
  const wait = d.items.filter((x) => !x.eligible);
  if (!pend.length && !wait.length) return <p className="text-xs text-muted-foreground" data-testid="rate-done">Ya evaluaste a este Maestro. ¡Gracias!</p>;
  return (
    <Card className="p-4 border-amber-300 bg-amber-50/60 dark:bg-amber-950/20" data-testid="rate-maestro-box">
      <p className="font-semibold text-sm flex items-center gap-1.5"><Star className="w-4 h-4 fill-amber-400 text-amber-400" />Evalúa a este Maestro</p>
      <div className="mt-2 space-y-2">
        {pend.map((x) => (
          <div key={x.reservation_id} className="flex items-center gap-2 justify-between">
            <span className="text-sm truncate">{x.workshop_name}</span>
            <Button size="sm" onClick={() => setOpen(x)} data-testid={`rate-open-${x.reservation_id}`}>Evaluar</Button>
          </div>
        ))}
        {wait.map((x) => (
          <div key={x.reservation_id} className="text-xs text-muted-foreground" data-testid={`rate-wait-${x.reservation_id}`}>
            <p className="truncate">{x.workshop_name}: asistencia {x.attendance_pct}% de {d.min_pct}% necesario</p>
            <div className="h-1.5 rounded-full bg-muted mt-1 overflow-hidden"><div className="h-full bg-primary" style={{ width: `${Math.min(x.attendance_pct / d.min_pct, 1) * 100}%` }} /></div>
          </div>
        ))}
      </div>
      <RateMaestroDialog item={open} onClose={() => setOpen(null)} onDone={() => { load(); onRated?.(); }} />
    </Card>
  );
}

export function MaestroReviewsCard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/business/maestro-reviews").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return null;
  return (
    <div className="space-y-3" data-testid="maestro-reviews-card">
      <h3 className="font-bold">Evaluaciones de tus alumnos</h3>
      <RatingSummary rep={d.summary} testId="maestro-own-reputation" />
      {d.items.filter((x) => x.comment).slice(0, 10).map((x) => (
        <Card key={x.id} className="p-3 text-sm" data-testid={`maestro-review-${x.id}`}>
          <div className="flex items-center justify-between gap-2"><span className="font-medium truncate">{x.customer_name} · {x.workshop_name}</span>
            <Stars value={PILLARS.reduce((a, p) => a + x[p.id], 0) / 4} size="w-3.5 h-3.5" /></div>
          <p className="text-muted-foreground mt-1 whitespace-pre-wrap">{x.comment}</p>
        </Card>
      ))}
      {d.summary.total > 0 && <p className="text-[11px] text-muted-foreground flex items-center gap-1"><Lock className="w-3 h-3" />Los comentarios solo los ves tú.</p>}
    </div>
  );
}
